import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary } from '../styles/theme.js'
import { piiScan as piiApi, projects as projectsApi } from '../lib/api.js'

// --- Styles ---

const pageWrap = css`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  a { color: ${colors.textSecondary}; }
`

const descCard = css`
  ${card};
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`

const patternList = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.xs};
`

const patternChip = css`
  font-size: ${font.size.xs};
  padding: 2px 8px;
  border-radius: 12px;
  background: ${colors.bgSidebar};
  border: 1px solid ${colors.border};
  color: ${colors.textSecondary};
`

const resultBox = css`
  padding: ${space.md} ${space.lg};
  background: ${colors.brandLight};
  border: 1px solid ${colors.brand};
  border-radius: 8px;
  font-size: ${font.size.sm};
`

const batchList = css`
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: ${colors.bgCard};
  overflow: hidden;
`

const batchRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${space.md} ${space.lg};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const batchInfo = css`
  flex: 1;
`

const batchTitle = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.sm};
`

const batchMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Component ---

export function PiiDetectionView() {
  let projectId = null
  let project = null
  let batches = []
  let loading = true
  let running = false
  let error = null
  let lastResult = null

  async function loadData() {
    loading = true
    m.redraw()
    try {
      batches = await piiApi.listBatches(projectId) || []
    } catch (err) {
      console.error('Failed to load PII scan batches:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function runScan() {
    running = true
    error = null
    lastResult = null
    m.redraw()
    try {
      const result = await piiApi.run(projectId)
      lastResult = result
      batches = await piiApi.listBatches(projectId) || []
    } catch (err) {
      error = err.message || 'Failed to run PII scan'
    } finally {
      running = false
      m.redraw()
    }
  }

  async function undoBatch(batchId) {
    try {
      await piiApi.undo(projectId, batchId)
      batches = await piiApi.listBatches(projectId) || []
      if (lastResult && lastResult.batchId === batchId) lastResult = null
    } catch (err) {
      error = err.message
    }
    m.redraw()
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadData()
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      return m('div', { class: pageWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'PII Detection',
        ]),

        m('h1', { style: `font-size: ${font.size['2xl']}; font-weight: ${font.weight.bold};` },
          'PII Detection'),

        error ? m('div', {
          style: `padding: ${space.md}; background: #fff0f0; border: 1px solid #ffcccc; border-radius: 6px; color: #cc0000; font-size: ${font.size.sm};`,
        }, error) : null,

        // Description + run button
        m('div', { class: descCard }, [
          m('p', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; margin: 0;` },
            'Scan all sessions for PII patterns. Detected text will be highlighted and automatically masked for non-owners.'),
          m('div', { class: patternList }, [
            'Email', 'Phone number', 'SSN', 'Credit card', 'IP address', 'Date of birth',
          ].map(name => m('span', { class: patternChip }, name))),
          m('button', {
            class: btnPrimary,
            disabled: running,
            onclick: runScan,
            style: 'align-self: flex-start;',
          }, running ? 'Scanning...' : 'Run PII Scan'),
        ]),

        // Last result
        lastResult ? m('div', { class: resultBox }, [
          lastResult.matchCount > 0
            ? `Found and highlighted ${lastResult.matchCount} PII match${lastResult.matchCount === 1 ? '' : 'es'}.`
            : 'No PII patterns detected.',
        ]) : null,

        // Scan history
        loading ? m('div', { class: emptyState }, 'Loading...') : batches.length > 0 ? [
          m('h2', { style: `font-size: ${font.size.lg}; font-weight: ${font.weight.semibold};` },
            'Scan History'),
          m('div', { class: batchList },
            batches.map(batch =>
              m('div', { key: batch.id, class: batchRow }, [
                m('div', { class: batchInfo }, [
                  m('div', { class: batchTitle }, 'PII Scan'),
                  m('div', { class: batchMeta },
                    `${batch.match_count} highlights · ${new Date(batch.created_at).toLocaleDateString()}`),
                ]),
                m('button', {
                  class: btnSecondary,
                  style: 'font-size: 0.75rem;',
                  onclick: () => undoBatch(batch.id),
                }, 'Undo'),
              ])
            )
          ),
        ] : null,
      ])
    },
  }
}
